import React from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';

/**
 * Global ErrorBoundary that catches uncaught render errors in child trees.
 * Prevents "blank white page" symptoms (e.g. an object rendered as a React
 * child) from taking down the whole app. Shows a friendly recovery screen
 * with a reload button and, in dev, the underlying error text.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so we can inspect stack traces in devtools; do NOT swallow.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught render error:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const isDev = process.env.NODE_ENV !== 'production';
    return (
      <div
        className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
        data-testid="error-boundary-screen"
      >
        <div className="max-w-lg w-full bg-white border border-red-200 rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">
              Ocorreu um erro inesperado
            </h1>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            A página encontrou um problema e não pôde continuar. Os seus dados
            não foram perdidos — recarregue a página para tentar novamente. Se
            o problema persistir, contacte o suporte.
          </p>
          {isDev && this.state.error && (
            <pre className="text-xs bg-slate-100 text-slate-700 rounded p-3 max-h-48 overflow-auto mb-4">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm transition"
            data-testid="error-boundary-reload"
          >
            <RotateCw className="w-4 h-4" />
            Recarregar página
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
